import { Box, TextButton } from '@zillow/constellation-10';
import type { CustomerOption } from './MainLayoutContext';
import {
  type CityOption,
  type MetropolitanServiceAreaOption,
  type StateOption,
  useMainLayout,
  type ZIPCodeOption,
} from './MainLayoutContext';
import { useEffect, useMemo, useState } from 'react';
import {
  getCitiesOptions,
  getCustomersOptions,
  getMetropolitanServiceAreasOptions,
  getPackagesOptions,
  getSpecialityHousingTypeOptions,
  getStatesOptions,
  getZIPCodesOptions,
} from './MainPageLayout.utils';
import type {
  ClientAdProductType,
  CustomerAnalyticsListingFilterType,
  CustomerAnalyticsSpecialityHousingType,
} from '@zg-rentals/ts-customer-analytics-client';
import { CustomFilterChipWithMenu } from '../../SharedComponents/CustomFilterChipWithMenu';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import {
  emitAnalyticsFilterChooseAdPackageEvent,
  emitAnalyticsFilterChooseCityEvent,
  emitAnalyticsFilterChooseMsaEvent,
  emitAnalyticsFilterChooseStateEvent,
  emitAnalyticsFilterChooseZipEvent,
  emitAnalyticsResetFiltersEvent,
} from '../../../analytics/events';

export const TopBarDropdowns = () => {
  const {
    allProperties,
    initialDataError,
    selectedFilters,
    setSelectedFilters,
    onSelectMetropolitanServiceArea,
    onSelectCity,
    onSelectZIPCode,
    onSelectState,
    onSelectPackage,
    onSelectCustomer,
    onSelectSpecialityHousingType,
    customers,
  } = useMainLayout();
  const { user } = useRentalManagerUser();

  const [allPackagesOptions, setAllPackagesOptions] = useState<
    Array<{ value: ClientAdProductType | CustomerAnalyticsListingFilterType; label: string }>
  >([]);
  const [allStatesOptions, setAllStatesOptions] = useState<StateOption>([]);
  const [allMetropolitanServiceAreasOptions, setAllMetropolitanServiceAreasOptions] =
    useState<MetropolitanServiceAreaOption>([]);
  const [allCitiesOptions, setAllCitiesOptions] = useState<CityOption>([]);
  const [allZIPCodesOptions, setAllZIPCodesOptions] = useState<ZIPCodeOption>([]);
  const [allCustomerOptions, setCustomerOptions] = useState<CustomerOption>([]);

  const specialityHousingTypeOptions = useMemo(
    () => getSpecialityHousingTypeOptions(allProperties, selectedFilters),
    [allProperties, selectedFilters],
  );

  useEffect(() => {
    if (allProperties.length > 0 && !initialDataError) {
      const allPackagesOptions = getPackagesOptions(allProperties, selectedFilters);
      const allStatesOptions = getStatesOptions(allProperties);
      const allMetropolitanServiceAreasOptions = getMetropolitanServiceAreasOptions(allProperties, selectedFilters);
      const allCitiesOptions = getCitiesOptions(allProperties, selectedFilters);
      const allZIPCodesOptions = getZIPCodesOptions(allProperties, selectedFilters);
      setAllPackagesOptions(allPackagesOptions);
      setAllStatesOptions(allStatesOptions);
      setAllMetropolitanServiceAreasOptions(allMetropolitanServiceAreasOptions);
      setAllCitiesOptions(allCitiesOptions);
      setAllZIPCodesOptions(allZIPCodesOptions);
      setCustomerOptions(getCustomersOptions(customers));
    }
  }, [allProperties, initialDataError, selectedFilters, customers]);

  const resetFilters = () => {
    if (user) emitAnalyticsResetFiltersEvent({ user });
    setSelectedFilters({
      package: '',
      stateRegionId: 0,
      metropolitanServiceAreaRegionId: 0,
      cityRegionId: 0,
      zipCodeRegionId: 0,
      listingAlias: '',
      customerId: '',
      specialityHousingType: '',
    });
  };

  return (
    <Box css={{ display: 'flex', flexWrap: 'wrap', paddingInline: '900', gap: '400' }}>
      <CustomFilterChipWithMenu
        selectedValue={selectedFilters.package}
        text="All advertising"
        options={[{ value: '', label: 'All advertising' }, ...allPackagesOptions]}
        onChange={(value) => {
          if (user) emitAnalyticsFilterChooseAdPackageEvent({ user });
          onSelectPackage(value as ClientAdProductType | CustomerAnalyticsListingFilterType);
        }}
        disabled={selectedFilters.listingAlias !== ''}
      />
      <CustomFilterChipWithMenu
        selectedValue={String(selectedFilters.stateRegionId)}
        text="All portfolio states"
        options={[
          { value: '0', label: 'All portfolio states' },
          ...allStatesOptions.map((option) => ({ value: String(option.value), label: option.label })),
        ]}
        onChange={(value) => {
          if (user) emitAnalyticsFilterChooseStateEvent({ user });
          const stateRegionId = Number(value);
          const selectedOption = allStatesOptions.find((option) => option.value === stateRegionId);
          if (selectedOption) {
            onSelectState(Number(selectedOption.value));
          } else {
            onSelectState(0);
          }
        }}
        disabled={selectedFilters.listingAlias !== ''}
      />
      <CustomFilterChipWithMenu
        selectedValue={String(selectedFilters.metropolitanServiceAreaRegionId)}
        text="All metropolitan service areas"
        options={[
          { value: '0', label: 'All metropolitan service areas' },
          ...allMetropolitanServiceAreasOptions.map((option) => ({ value: String(option.value), label: option.label })),
        ]}
        onChange={(value) => {
          if (user) emitAnalyticsFilterChooseMsaEvent({ user });
          const metropolitanServiceAreaRegionId = Number(value);
          const selectedOption = allMetropolitanServiceAreasOptions.find(
            (option) => option.value === metropolitanServiceAreaRegionId,
          );
          if (selectedOption) {
            onSelectMetropolitanServiceArea(selectedOption.value, selectedOption.stateRegionId);
          } else {
            onSelectMetropolitanServiceArea(0, Number(selectedFilters.stateRegionId));
          }
        }}
        disabled={selectedFilters.listingAlias !== ''}
      />
      <CustomFilterChipWithMenu
        selectedValue={String(selectedFilters.cityRegionId)}
        text="All cities"
        options={[
          { value: '0', label: 'All cities' },
          ...allCitiesOptions.map((option) => ({ value: String(option.value), label: option.label })),
        ]}
        onChange={(value) => {
          if (user) emitAnalyticsFilterChooseCityEvent({ user });
          const cityRegionId = Number(value);
          const selectedOption = allCitiesOptions.find((option) => option.value === cityRegionId);
          if (selectedOption) {
            onSelectCity(
              selectedOption.value,
              selectedOption.metropolitanServiceAreaRegionId,
              selectedOption.stateRegionId,
            );
          } else {
            onSelectCity(
              0,
              Number(selectedFilters.metropolitanServiceAreaRegionId),
              Number(selectedFilters.stateRegionId),
            );
          }
        }}
        disabled={selectedFilters.listingAlias !== ''}
      />
      <CustomFilterChipWithMenu
        selectedValue={String(selectedFilters.zipCodeRegionId)}
        text="All ZIP codes"
        options={[
          { value: '0', label: 'All ZIP codes' },
          ...allZIPCodesOptions.map((option) => ({ value: String(option.value), label: option.label })),
        ]}
        onChange={(value) => {
          if (user) emitAnalyticsFilterChooseZipEvent({ user });
          const zipCodeRegionId = Number(value);
          const selectedOption = allZIPCodesOptions.find((option) => option.value === zipCodeRegionId);
          if (selectedOption) {
            onSelectZIPCode(
              selectedOption.value,
              selectedOption.cityRegionId,
              selectedOption.metropolitanServiceAreaRegionId,
              selectedOption.stateRegionId,
            );
          } else {
            onSelectZIPCode(
              0,
              Number(selectedFilters.cityRegionId),
              Number(selectedFilters.metropolitanServiceAreaRegionId),
              Number(selectedFilters.stateRegionId),
            );
          }
        }}
        disabled={selectedFilters.listingAlias !== ''}
      />
      {customers.length > 1 && (
        <CustomFilterChipWithMenu
          selectedValue={String(selectedFilters.customerId)}
          text="All companies"
          options={[
            { value: '', label: 'All companies' },
            ...allCustomerOptions.map((option) => ({ value: String(option.value), label: option.label })),
          ]}
          onChange={(value) => {
            const customerId = value;
            const selectedOption = allCustomerOptions.find((option) => option.value === customerId);
            if (selectedOption) {
              onSelectCustomer(selectedOption.value);
            } else {
              onSelectCustomer('');
            }
          }}
        />
      )}
      <CustomFilterChipWithMenu
        selectedValue={selectedFilters.specialityHousingType}
        text="All housing types"
        options={[{ value: '', label: 'All housing types' }, ...specialityHousingTypeOptions]}
        onChange={(value) => {
          onSelectSpecialityHousingType(value as CustomerAnalyticsSpecialityHousingType | '');
        }}
        disabled={selectedFilters.listingAlias !== ''}
      />
      {/* <SavedViewsDropdown /> */}
      <TextButton textStyle="body-bold" asChild>
        <a onClick={resetFilters}>Reset filters</a>
      </TextButton>
    </Box>
  );
};
